import {
  Empty,
  ErrorState,
  Loading,
  PageHead,
  PriorityBadge,
  Select,
  StatusBadge,
  useFormat,
} from '@ly360/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, ShieldAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams } from 'react-router';
import { CRM_KEYS, type CaseRow, type TaskRow } from '../crm/data';
import { Field, NewCaseDialog, NewTaskDialog, UserSelect } from '../crm/forms';
import { TaskList } from '../crm/TaskList';
import { Notes } from '../crm/Timeline';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';

export function CasesPage() {
  const { t } = useTranslation();
  const { can } = useAuth();
  const fmt = useFormat();
  const navigate = useNavigate();
  const [status, setStatus] = useState('active');
  const [assignee, setAssignee] = useState('any');
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const params = new URLSearchParams(Object.entries({ status, assignee, q }).filter(([, v]) => v));
  const list = useQuery({
    queryKey: ['cases', status, assignee, q],
    queryFn: () => api<{ items: CaseRow[] }>(`/v1/cases?${params}`),
  });
  return (
    <>
      <PageHead
        eyebrow={t('nav.main')}
        title={t('crm.cases.title')}
        subtitle={t('crm.cases.subtitle')}
        actions={
          can('cases:write') && (
            <button className="btn btn-grad" onClick={() => setOpen(true)} data-testid="add-case">
              <Plus size={16} />
              {t('crm.cases.new')}
            </button>
          )
        }
      />
      <section className="card">
        <div className="toolbar">
          <input
            className="input"
            type="search"
            placeholder={t('common.search')}
            aria-label={t('common.search')}
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          <Select
            value={status}
            onChange={setStatus}
            aria-label={t('crm.cases.col.status')}
            options={[
              { value: 'active', label: t('crm.cases.filter.active') },
              ...(['new', 'open', 'pending', 'resolved', 'closed'] as const).map((s) => ({
                value: s,
                label: t(`crm.caseStatus.${s}`),
              })),
              { value: 'all', label: t('crm.cases.filter.all') },
            ]}
          />
          <Select
            value={assignee}
            onChange={setAssignee}
            aria-label={t('crm.cases.col.assignee')}
            options={[
              { value: 'any', label: t('crm.cases.filter.any') },
              { value: 'me', label: t('crm.cases.filter.mine') },
              { value: 'unassigned', label: t('crm.cases.filter.unassigned') },
            ]}
          />
        </div>
        {list.isLoading && <Loading />}
        {list.isError && <ErrorState error={list.error} />}
        {list.data?.items.length === 0 && <Empty text={t('crm.cases.empty')} />}
        {!!list.data?.items.length && (
          <div className="table-wrap">
            <table className="table table-responsive" data-testid="cases-table">
              <thead>
                <tr>
                  <th>{t('crm.cases.col.number')}</th>
                  <th>{t('crm.cases.col.subject')}</th>
                  <th>{t('crm.cases.col.customer')}</th>
                  <th>{t('crm.cases.col.priority')}</th>
                  <th>{t('crm.cases.col.status')}</th>
                  <th>{t('crm.cases.col.assignee')}</th>
                  <th>
                    {t('crm.cases.col.created')} <span className="faint">({fmt.zoneCaption})</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {list.data.items.map((c) => (
                  <tr key={c.id} onClick={() => navigate(`/cases/${c.id}`)} style={{ cursor: 'pointer' }}>
                    <td className="num" data-label={t('crm.cases.col.number')}>
                      {c.number}
                    </td>
                    <td data-label={t('crm.cases.col.subject')}>
                      <Link to={`/cases/${c.id}`} onClick={(e) => e.stopPropagation()} dir="auto">
                        <strong>{c.subject}</strong>
                      </Link>
                      {c.regulatorFlag && (
                        <ShieldAlert
                          size={14}
                          style={{ marginInlineStart: 6, verticalAlign: '-2px' }}
                          color="var(--danger)"
                          aria-label="regulator"
                        />
                      )}
                    </td>
                    <td data-label={t('crm.cases.col.customer')} dir="auto">
                      {c.personName ?? c.accountName ?? '—'}
                    </td>
                    <td data-label={t('crm.cases.col.priority')}>
                      <PriorityBadge value={c.priority} />
                    </td>
                    <td data-label={t('crm.cases.col.status')}>
                      <StatusBadge value={c.status} ns="crm.caseStatus" />
                    </td>
                    <td data-label={t('crm.cases.col.assignee')}>
                      {c.assigneeName ?? <span className="faint">{t('common.nobody')}</span>}
                    </td>
                    <td data-label={t('crm.cases.col.created')} style={{ whiteSpace: 'nowrap' }}>
                      {fmt.dateTime(c.createdAt)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {open && <NewCaseDialog open onClose={() => setOpen(false)} />}
    </>
  );
}

export function CasePage() {
  const { id = '' } = useParams();
  const { t } = useTranslation();
  const { can } = useAuth();
  const fmt = useFormat();
  const qc = useQueryClient();
  const [taskOpen, setTaskOpen] = useState(false);
  const q = useQuery({ queryKey: ['case', id], queryFn: () => api<{ case: CaseRow }>(`/v1/cases/${id}`) });
  const tasks = useQuery({
    queryKey: ['tasks', 'case', id],
    queryFn: () =>
      api<{ items: TaskRow[] }>(`/v1/tasks?assignee=any&status=all&relatedType=case&relatedId=${id}`),
  });
  const update = useMutation({
    mutationFn: (patch: Record<string, unknown>) =>
      api(`/v1/cases/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    onSuccess: () => CRM_KEYS.forEach((k) => void qc.invalidateQueries({ queryKey: [k] })),
  });
  if (q.isLoading) return <Loading />;
  if (q.isError || !q.data) return <ErrorState error={q.error} />;
  const c = q.data.case;
  const editable = can('cases:write');
  return (
    <>
      <div className="record-head">
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="eyebrow">
            {t('crm.cases.title')} #{c.number}
          </div>
          <h1 dir="auto">{c.subject}</h1>
          <div className="sub">
            {c.personId && (
              <Link to={`/people/${c.personId}`} dir="auto">
                {c.personName}
              </Link>
            )}
            {c.accountName && <> · {c.accountName}</>} · {fmt.dateTimeZoned(c.createdAt)}
          </div>
        </div>
        <PriorityBadge value={c.priority} />
        <StatusBadge value={c.status} ns="crm.caseStatus" />
      </div>
      <div className="record-grid">
        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
          {c.description && (
            <section className="card card-pad">
              <div dir="auto" style={{ whiteSpace: 'pre-wrap' }}>
                {c.description}
              </div>
            </section>
          )}
          <Notes relatedType="case" relatedId={id} />
        </div>
        <div style={{ display: 'grid', gap: '1rem', minWidth: 0 }}>
          <section className="card card-pad" aria-label={t('crm.cases.details')}>
            <h2 className="section-title">{t('crm.cases.details')}</h2>
            <fieldset
              disabled={!editable || update.isPending}
              style={{ border: 0, padding: 0, margin: 0, minWidth: 0 }}
            >
              <Field id="cs-status" label={t('crm.cases.col.status')}>
                <Select
                  id="cs-status"
                  value={c.status}
                  onChange={(v) => update.mutate({ status: v })}
                  data-testid="case-status"
                  options={(['new', 'open', 'pending', 'resolved', 'closed'] as const).map((s) => ({
                    value: s,
                    label: t(`crm.caseStatus.${s}`),
                  }))}
                />
              </Field>
              <Field id="cs-priority" label={t('crm.cases.col.priority')}>
                <Select
                  id="cs-priority"
                  value={c.priority}
                  onChange={(v) => update.mutate({ priority: v })}
                  options={(['low', 'normal', 'high', 'urgent'] as const).map((p) => ({
                    value: p,
                    label: t(`crm.priority.${p}`),
                  }))}
                />
              </Field>
              <Field id="cs-assignee" label={t('crm.cases.field.assignee')}>
                <UserSelect
                  id="cs-assignee"
                  value={c.assigneeUserId ?? ''}
                  onChange={(v) => update.mutate({ assigneeUserId: v || null })}
                />
              </Field>
            </fieldset>
            {c.resolvedAt && (
              <p className="faint" style={{ fontSize: 'var(--t-xs)' }}>
                {t('crm.cases.resolvedAt')}: {fmt.dateTimeZoned(c.resolvedAt)}
              </p>
            )}
            {update.isError && <ErrorState error={update.error} />}
          </section>
          <section className="card">
            <div className="card-head">
              <h2>{t('crm.person.tasks')}</h2>
              {can('tasks:write') && (
                <button
                  className="icon-btn"
                  onClick={() => setTaskOpen(true)}
                  aria-label={t('crm.tasks.new')}
                  title={t('crm.tasks.new')}
                >
                  <Plus size={16} />
                </button>
              )}
            </div>
            <TaskList items={tasks.data?.items ?? []} showRelated={false} />
          </section>
        </div>
      </div>
      {taskOpen && (
        <NewTaskDialog open onClose={() => setTaskOpen(false)} relatedType="case" relatedId={id} />
      )}
    </>
  );
}
